/**
 * Wires the event log into the rails the app reports on.
 *
 * Every tap is deliberately placed at the OUTERMOST boundary -- the mixpanel singleton,
 * the axios default instance, and `window.fetch` -- rather than inside MixpanelService or
 * api.js. Every caller in the app goes through those objects (four files import
 * `mixpanel-browser`, everything else calls a service; `api.js` and
 * `AccessoryMultiSelect` both use the bare axios default), so tapping there means the
 * panel shows what actually left the browser, including anything a future call site adds
 * without knowing this exists.
 *
 * `fetch` is tapped as well as axios because the funnel rail does not use axios:
 * `FunnelService.post` needs `keepalive` so the hero CTA outlives the navigation, and
 * neither axios nor sendBeacon can set that (sendBeacon cannot send the CSRF header
 * either). Without a fetch tap, `/api/refi/funnelEvent` -- the whole funnel rail, and the
 * primary metric for every landing-page test -- was the one thing the panel could not
 * show.
 *
 * No tap may change behaviour. The mixpanel wrapper always forwards, the axios
 * interceptors always return the config or re-throw the error, the fetch wrapper always
 * resolves with the real response or re-throws the real error, and every recording call
 * is inside a try/catch: an exception raised here would surface as a broken step
 * transition or a failed save, which is a far worse outcome than a missing log line.
 */
import {complete, record} from './eventLog';

/** `/api/refi/funnelEvent` -> `funnelEvent`, for a readable label. */
const shortName = url => String(url || '').replace(/^\/api\/(refi|landingpage)\//, '').split('?')[0];

const parseBody = data => {
    if (typeof data !== 'string') {
        return data;
    }

    try {
        return JSON.parse(data);
    } catch (ex) {
        return data;
    }
};

export const tapMixpanel = mixpanel => {
    const methods = {
        identify: id => ({distinctId: id}),
        register: props => props,
        track: (name, props) => props
    };

    for (const [method, extract] of Object.entries(methods)) {
        const original = mixpanel[method];

        if (typeof original !== 'function' || original.__tapped) {
            continue;
        }

        const wrapped = function(...args) {
            try {
                record({
                    data: extract(...args) || {},
                    name: method === 'track' ? args[0] : method,
                    rail: 'mixpanel'
                });
            } catch (ex) {
                // Never let the log break analytics, let alone the page.
            }

            return original.apply(this, args);
        };

        wrapped.__tapped = true;
        mixpanel[method] = wrapped;
    }

    // people.set lives one level down and carries profile attributes.
    const people = mixpanel.people;

    if (people && typeof people.set === 'function' && !people.set.__tapped) {
        const originalSet = people.set;
        const wrappedSet = function(...args) {
            try {
                record({data: args[0] || {}, name: 'people.set', rail: 'mixpanel'});
            } catch (ex) {
                // as above
            }

            return originalSet.apply(this, args);
        };

        wrappedSet.__tapped = true;
        people.set = wrappedSet;
    }

    return mixpanel;
};

/**
 * @param {Object} scope  defaults to `window`; injectable for tests
 */
export const tapFetch = (scope = typeof window === 'undefined' ? undefined : window) => {
    const original = scope && scope.fetch;

    if (typeof original !== 'function' || original.__tapped) {
        return original;
    }

    const wrapped = function(...args) {
        const [resource, init = {}] = args;
        const startedAt = Date.now();

        let logId;

        try {
            // A Request object rather than a URL string is legal and carries its own url.
            const url = typeof resource === 'string' ? resource : resource && resource.url;
            const entry = record({
                data: parseBody(init.body) || {},
                method: String(init.method || 'get').toUpperCase(),
                name: shortName(url),
                rail: 'server',
                url
            });

            logId = entry.id;
        } catch (ex) {
            // Never let the log break a request.
        }

        // `apply(scope)` rather than `call(this)`: native fetch throws "Illegal
        // invocation" when it is called detached from its global, which is exactly what
        // happens to anything that destructures `fetch` off the window.
        return original.apply(scope, args).then(
            response => {
                try {
                    complete(logId, {
                        ms: Date.now() - startedAt,
                        status: response.status
                    });
                } catch (ex) {
                    // as above
                }

                return response;
            },
            error => {
                try {
                    complete(logId, {
                        failed: true,
                        ms: Date.now() - startedAt,
                        // A fetch rejection has no response at all -- a distinct outcome
                        // from an HTTP error status, and the panel should say so.
                        status: 'no response'
                    });
                } catch (ex) {
                    // as above
                }

                throw error;
            }
        );
    };

    wrapped.__tapped = true;
    scope.fetch = wrapped;

    return wrapped;
};

export const tapAxios = axios => {
    if (axios.__tapped) {
        return axios;
    }

    axios.interceptors.request.use(config => {
        try {
            const entry = record({
                data: parseBody(config.data) || parseBody(config.params) || {},
                method: String(config.method || 'get').toUpperCase(),
                name: shortName(config.url),
                rail: 'server',
                url: config.url
            });

            config.__logId = entry.id;
            config.__startedAt = Date.now();
        } catch (ex) {
            // as above
        }

        return config;
    });

    axios.interceptors.response.use(
        response => {
            try {
                complete(response.config?.__logId, {
                    ms: Date.now() - (response.config?.__startedAt || Date.now()),
                    status: response.status
                });
            } catch (ex) {
                // as above
            }

            return response;
        },
        error => {
            try {
                const config = error?.config || {};

                complete(config.__logId, {
                    failed: true,
                    ms: Date.now() - (config.__startedAt || Date.now()),
                    // A cancelled request has no response; that is a distinct outcome
                    // from a rejected one and the panel should say so.
                    status: error?.response?.status ?? 'no response'
                });
            } catch (ex) {
                // as above
            }

            return Promise.reject(error);
        }
    );

    axios.__tapped = true;

    return axios;
};
